/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions*/

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";

import { LoaderContext } from "@fitinsur/contexts";
import { useTextMessage } from "@fitinsur/hooks";
import { LineLabel } from "./styles";

import { useGetAdminDocumentParamsList } from "@fitinsur/services/AdminService";

import Colors from "constants/Colors";

import {
  Button,
  Text,
  ResponsiveTable,
  ListHeader,
  ListActions,
} from "@fitinsur/components";

import * as S from "./styles";

export function DocumentParamsList({ permissions }) {
  const { getDescription } = useTextMessage();
  const { updateIsLoadingStatus } = useContext(LoaderContext);

  const history = useHistory();
  const formik = useRef();

  const columns = [
    {
      title: getDescription("DOMAINS", 822, { textFormat: "uppercase" }),
      key: "documentParamsDescription",
      sortKey: "documentParamsDescription",
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => (
        <LineLabel>
          {`${listItem.idDomain} - ${listItem.domainDescription}`}
        </LineLabel>
      ),
    },
     {
      title: getDescription("DOMAINS", 1311, { textFormat: "uppercase" }),
      key: "userName",
      sortKey: "userName",
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => (
        <LineLabel>
          {listItem.userName || '-'}
        </LineLabel>
      ),
    },
    {
      title: getDescription("DOMAINS", 1310, { textFormat: 'uppercase' }),
      key: "attachmentFileName",
      sortKey: "attachmentFileName",
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => (
        <LineLabel>
          {listItem.attachmentFileName || '-'}
        </LineLabel>
      ),
    },
    {
      title: getDescription("DOMAINS", 1309, { textFormat: 'uppercase' }),
      key: "systemUpdateDate",
      sortKey: "systemUpdateDate",
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => (
        <LineLabel>
          {new Date(listItem.systemUpdateDate).toLocaleDateString("pt-BR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </LineLabel>
      ),
    },
    {
      title: getDescription("DOMAINS", 783, { textFormat: "uppercase" }),
      key: "action",
      minWidth: 8,
      maxWidth: 8,
      paddingRight: 25,
      component: (listItem) => (
        <ListActions
          editRoute={
            permissions.EDIT_DOCUMENT_PARAMETERS
              ? () =>
                  history.push(
                    `/management/58/document-params/edit/${listItem.idDomain}`
                  )
              : null
          }
        />
      ),
    },
  ];

  const initialFilterData = [
    {
      label: getDescription("DOMAINS", 822, { textFormat: "uppercase" }),
      orderKey: "documentParamsDescription",
      key: "documentParamsDescription",
      type: "text",
    },
    {
      label: getDescription("DOMAINS", 1311, { textFormat: "uppercase" }),
      orderKey: "userName",
      key: "userName",
      type: "text",
    },
    {
      label: getDescription("DOMAINS", 1310, { textFormat: "uppercase" }),
      orderKey: "attachmentFileName",
      key: "attachmentFileName",
      type: "text",
    },
    {
      label: getDescription("DOMAINS", 1309, { textFormat: "uppercase" }),
      orderKey: "systemUpdateDate",
      key: "systemUpdateDate",
      type: "date",
    },
  ];

  const storedFilterOption = localStorage.getItem("document-params-filter");
  const storedStringFilterOption = localStorage.getItem(
    "str-document-params-filter"
  );

  let quotesUrl =
    "/management/58/document-params" +
    (storedStringFilterOption ? "?" + storedStringFilterOption : "");

  const [filterData, setFilterData] = useState(
    storedFilterOption ? JSON.parse(storedFilterOption) : initialFilterData
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listData, setListData] = useState([]);

  const [paginationControls, setPaginationControls] = useState({
    currentPage: 1,
    numberMaxRecords: null,
    total: null,
    totalPages: null,
    numberRecords: null,
  });

  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const initialValues = {
    filter: {
      documentParamsDescription: "",
      userName: "",
      attachmentFileName: "",
      systemUpdateDate: "",
    },
  };

  const { loading: useGetListLoading, request: useGetListRequest } =
    useGetAdminDocumentParamsList();

  async function getListData(resetFilter) {
    const refFilterData = resetFilter ? initialFilterData : filterData;
    useGetListRequest(null, {
      page: paginationControls.currentPage,
      sortBy,
      ...refFilterData
        .filter((v) => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a;
        }, {}),
    }).then((resp) => {
      const {
        data: {
          value: { documents, pagination },
        },
      } = resp;

      if (paginationControls.currentPage === 1) {
        setListData(documents);
      } else {
        setListData([...listData, ...documents]);
        setCurrentPage(paginationControls.currentPage);
      }

      setTotalPages(pagination?.totalPages);
    });
  }

  function handleSortList(key, direction) {
    if (!key || key === "action" || key === "status") return;
    direction = direction === 1 ? "DESC" : "ASC";
    let sort = { key, direction };
    setSortBy(sort);
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);
    getListData();
  }

  function handleFilter(resetFilter) {
    quotesUrl =
      "/management/58/document-params" +
      (localStorage.getItem("document-params-filter")
        ? "?" + localStorage.getItem("str-document-params-filter")
        : "");
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);

    if (resetFilter) {
      getListData(resetFilter);
      setSortBy({ key: null, direction: null });
      localStorage.removeItem("document-params-filter");
      localStorage.removeItem("str-document-params-filter");
      history.push(`/management/58/document-params`);
    } else {
      getListData();
      history.push(`${quotesUrl}`);
    }
  }

  function setNextPage(paginationControls) {
    let e = paginationControls;
    e.currentPage++;
    setPaginationControls(e);
    getListData();
  }

  useEffect(() => {
    getListData();
  }, []);

  useEffect(() => {
    updateIsLoadingStatus(useGetListLoading);
  }, [useGetListLoading]);

  return (
    <S.Content>
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => handleFilter(values)}
        innerRef={formik}
      >
        {() => (
          <Form>
            <ListHeader
              isAdmin
              filterData={filterData}
              setFilterData={setFilterData}
              onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
              listNameSpace={"document-params-filter"}
              listUrl="/management/58/document-params"
              idDomainList={996}
              idApplication={675}
              filterNamePrefix={getDescription("DOMAINS", 1074, {
                textFormat: "uppercase",
              })}
              defaultFilterName={getDescription("DOMAINS", 1075, {
                textFormat: "uppercase",
              })}
            />
            <>
              <ResponsiveTable
                onSortTable={handleSortList}
                formikRef={formik?.current}
                columns={columns}
                data={listData}
              />

              {!listData.length && (
                <S.ButtonContainer style={{ minHeight: "450px" }}>
                  <Text
                    style={{
                      fontWeight: "bold",
                      color: Colors.GRAY[1],
                      margin: "auto",
                      cursor: "pointer",
                      marginTop: "10px",
                    }}
                    onClick={() => {}}
                  >
                    {getDescription("DOMAINS", 786)}
                  </Text>
                </S.ButtonContainer>
              )}
            </>
          </Form>
        )}
      </Formik>

      <S.LoadContainer>
        {!listData?.length || currentPage === totalPages ? (
          <Button forth onClick={(e) => e.preventDefault()}>
            {getDescription("DOMAINS", 784, { textFormat: "uppercase" })}
          </Button>
        ) : (
          <Button forth onClick={() => setNextPage(paginationControls)}>
            + {getDescription("DOMAINS", 785, { textFormat: "uppercase" })}
          </Button>
        )}
      </S.LoadContainer>
    </S.Content>
  );
}
